import { Lookup } from '../contexts/MetadataContext';
export const STATE_MAPPINGS = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
  'DISTRICT OF COLUMBIA': 'DC',
  'AMERICAN SAMOA': 'AS',
  GUAM: 'GU',
  'NORTHERN MARIANA ISLANDS': 'MP',
  'PUERTO RICO': 'PR',
  'VIRGIN ISLANDS': 'VI',
};

/**
 * @typedef {Object} LocationPartial
 * @property {string} [state] - The state/province code (e.g., 'CA', 'NY')
 * @property {string} [city] - The city name
 * @property {string} [zip] - The postal/zip code
 * @property {string} [street] - The street address
 * @property {string} [county] - The location county
 *
 * Note: Properties are optional as location data may be incomplete
 */
export interface LocationPartial {
  state?: string;
  county?: string;
  city?: string;
  zip?: string;
  street?: string;
}

/**
 * Simple point representation
 */
export interface Point {
  x: number;
  y: number;
}

/**
 *
 * formatCounty("Baltimore (City)") // "Baltimore City"
 * formatCounty("WASHINGTON DC") // "WASHINGTON"
 * formatCounty("Prince George's (County)") // "Prince George County"
 *
 */
export function formatCounty(
  county: string,
  state?: string,
  countyResolutionMap?: string,
) {
  if (!county) return '';

  // Normalize to uppercase for consistent handling
  county = county.toUpperCase();
  const normalizedState = state?.toUpperCase();

  if (countyResolutionMap) {
    try {
      // Format: JSON array of rules
      // [
      //   { from: "WASHINGTON DC", to: "WASHINGTON" },
      //   { from: "ST MARY", to: "SAINT MARY", state: "MD" }
      // ]
      const rules = JSON.parse(countyResolutionMap);

      for (const rule of rules) {
        if (rule.state) {
          // State-specific rule
          if (normalizedState === rule.state && county === rule.from) {
            county = rule.to;
            break;
          }
        } else {
          // Global rule
          if (county === rule.from) {
            county = rule.to;
            break;
          }
        }
      }
    } catch (e) {
      console.error('Invalid county resolution map format:', e);
    }
  }

  // Remove apostrophes
  county = county.replaceAll("'", '');

  // Remove outer parentheses and keep content
  county = county.replace(/\((.*?)\)/g, '$1');

  // Remove "COUNTY" from the end (case-insensitive)
  county = county.replace(/\s+COUNTY$/i, '');

  // Clean up any extra spaces and trim
  county = county.replace(/\s+/g, ' ').trim();

  return county;
}

export function formatState(state: string) {
  if (!state) return '';

  // If it's already a 2-letter code in correct format, return it
  if (/^[A-Z]{2}$/.test(state)) {
    return state;
  }

  // Normalize the input by removing extra spaces and converting to uppercase
  const normalizedState = state.trim().toUpperCase();
  const mappings: any = STATE_MAPPINGS;
  // If it's in the mappings, return the abbreviation
  if (mappings[normalizedState]) {
    return mappings[normalizedState];
  }

  // If it's not found, try to find a partial match
  const matchingState = Object.keys(mappings).find((fullName) =>
    fullName.includes(normalizedState),
  );

  if (matchingState) {
    return mappings[matchingState];
  }

  // If no match is found, return the original input but warn in console
  console.warn(`Unable to format state: ${state}`);
  return state;
}

export function formatZipCode(postalCode: string) {
  if (!postalCode) return { base: '', plus4: '' };

  const parts = postalCode.split('-');
  return {
    base: parts[0],
    plus4: parts.length > 1 ? parts[1] : '',
  };
}

export function formatAddressLineOne(addressParts: {
  streetName?: string;
  streetDirPrefix?: string;
  streetNumber?: string;
  streetSuffix?: string;
  streetDirSuffix?: string;
  streetModifier?: string;
  unitNumber?: string;
}) {
  const {
    streetName,
    streetNumber,
    streetDirPrefix,
    streetSuffix,
    streetDirSuffix,
    streetModifier,
    unitNumber,
  } = addressParts;
  // Get abbreviated form of street suffix if it exists, otherwise use the original
  const abbreviatedSuffix = streetSuffix
    ? commonAbbreviations[streetSuffix] || streetSuffix
    : '';

  const formattedStreetNumber = streetModifier
    ? `${streetNumber}-${streetModifier}`
    : streetNumber;

  return [
    formattedStreetNumber,
    streetDirPrefix,
    streetName,
    abbreviatedSuffix,
    streetDirSuffix,
    !unitNumber ? '' : `#${unitNumber}`,
  ]
    .filter(Boolean)
    .join(' ');
}

export function formatAddressLineTwo(addressParts: {
  city: string;
  state: string;
  zipCode: string;
  zipCodePlus4: string;
}) {
  const { city, state, zipCode, zipCodePlus4 } = addressParts;
  const formattedZipCodePlus4 = zipCodePlus4 ? `-${zipCodePlus4}` : '';

  return [city, state, `${zipCode}${formattedZipCodePlus4}`]
    .filter(Boolean)
    .join(', ');
}

interface StreetParts {
  name: string;
  dir_prefix: string;
  dir_suffix: string;
  suffix: string;
}

const commonAbbreviations: Record<string, string> = {
  AVENUE: 'AVE',
  BOULEVARD: 'BLVD',
  CIRCLE: 'CIR',
  COURT: 'CT',
  DRIVE: 'DR',
  EXPRESSWAY: 'EXPY',
  HEIGHTS: 'HTS',
  HIGHWAY: 'HWY',
  JUNCTION: 'JCT',
  LANE: 'LN',
  PARKWAY: 'PKWY',
  PLACE: 'PL',
  ROAD: 'RD',
  SQUARE: 'SQ',
  STREET: 'ST',
  TERRACE: 'TER',
  TRAIL: 'TRL',
  TURNPIKE: 'TPKE',
  FREEWAY: 'FWY',
  WAY: 'WAY', // Some we might want to keep as is
  PIER: 'PIER',
  ROW: 'ROW',
  ALLEY: 'ALY',
  EXTENSION: 'EXT',
  CRESCENT: 'CRES',
  CROSSING: 'XING',
};

function findBestMatch(word: string, lookupValues: Lookup[]): Lookup | null {
  // Convert test word to uppercase for comparison
  const testWord = word.toUpperCase();

  // First try exact match
  const exactMatch = lookupValues.find((l) => l.LookupValue === testWord);
  if (exactMatch) return exactMatch;

  // Common abbreviations mapping
  const commonAbbreviations: Record<string, string[]> = {
    STREET: ['ST'],
    AVENUE: ['AVE'],
    BOULEVARD: ['BLVD'],
    CIRCLE: ['CIR'],
    COURT: ['CT'],
    DRIVE: ['DR'],
    LANE: ['LN'],
    PLACE: ['PL'],
    ROAD: ['RD'],
    SQUARE: ['SQ'],
    TERRACE: ['TER'],
    PARKWAY: ['PKWY'],
    HIGHWAY: ['HWY'],
  };

  // Check if the test word is a common abbreviation
  for (const [full, abbrevs] of Object.entries(commonAbbreviations)) {
    if (abbrevs.includes(testWord)) {
      const match = lookupValues.find(
        (l) => l.LookupValue === full || l.DisplayValue === full,
      );
      if (match) return match;
    }
  }

  // If no match found through abbreviations, try partial match
  // This is a more lenient fallback
  return (
    lookupValues.find(
      (l) =>
        l.LookupValue.startsWith(testWord) ||
        l.DisplayValue?.startsWith(testWord) ||
        testWord.startsWith(l.LookupValue) ||
        (l.DisplayValue && testWord.startsWith(l.DisplayValue)),
    ) || null
  );
}

function findDirectionMatch(word: string, directions: Lookup[]): Lookup | null {
  const testWord = word.toUpperCase();
  // Only exact matches for directions
  return directions.find((d) => d.LookupValue === testWord) || null;
}

export function formatStreet(
  street: string,
  lookups: {
    street_types: Lookup[];
    street_directions: Lookup[];
  },
): StreetParts {
  // Return empty object if no street provided
  if (!street) return { name: '', dir_prefix: '', dir_suffix: '', suffix: '' };

  // Split the street into words
  const words = street.trim().split(/\s+/);
  if (words.length === 0)
    return { name: '', dir_prefix: '', dir_suffix: '', suffix: '' };

  let result: StreetParts = {
    name: '',
    dir_prefix: '',
    dir_suffix: '',
    suffix: '',
  };

  // Check first word for direction prefix (using strict matching)
  const firstWordDirection = findDirectionMatch(
    words[0],
    lookups.street_directions,
  );
  let startNameIndex = 0;

  if (firstWordDirection) {
    result.dir_prefix =
      firstWordDirection.DisplayValue || firstWordDirection.LookupValue;
    startNameIndex = 1;
  }

  // Check last word for direction suffix (using strict matching)
  const lastWordDirection = findDirectionMatch(
    words[words.length - 1],
    lookups.street_directions,
  );
  let endNameIndex = words.length;

  if (
    lastWordDirection &&
    (!firstWordDirection || words[0] !== words[words.length - 1])
  ) {
    result.dir_suffix =
      lastWordDirection.DisplayValue || lastWordDirection.LookupValue;
    endNameIndex--;
  }

  // Check for street type (working backwards from end, excluding direction suffix)
  // Street types can still use the more permissive findBestMatch
  let suffixIndex = endNameIndex - 1;
  const typeMatch = findBestMatch(words[suffixIndex], lookups.street_types);

  if (typeMatch) {
    result.suffix = typeMatch.DisplayValue || typeMatch.LookupValue;
    endNameIndex = suffixIndex;
  }

  // Whatever's left in the middle is the name
  result.name = words.slice(startNameIndex, endNameIndex).join(' ');

  return result;
}

/**
 * Performs a loose comparison between two location objects to determine if they match.
 *
 * This function compares state information between two location objects to validate
 * if they represent the same geographic area. The comparison is intentionally lenient
 * to avoid discarding potentially valid location search results.
 *
 * @param {LocationPartial} locationA - First location object, typically from external geoservice provider
 * @param {LocationPartial} locationB - Second location object, typically from local public records
 *
 * @returns {boolean} True if locations are considered a match, false otherwise
 *
 * @example
 * // Example with matching states
 * const geoServiceLocation = { state: 'CA', city: 'San Francisco' };
 * const localRecordLocation = { state: 'CA', city: 'SF' };
 * const isMatch = isValidLocationMatch(geoServiceLocation, localRecordLocation); // returns true
 *
 * @example
 * // Special case for District of Columbia
 * const dcLocation = { state: 'DC', city: 'Washington' };
 * const otherLocation = { state: 'MD', city: 'Baltimore' };
 * const isMatch = isValidLocationMatch(dcLocation, otherLocation); // returns true due to DC exception
 *
 * @notes
 * - Special handling exists for District of Columbia due to data quality issues
 * - Function returns true if either location has missing state data to avoid false negatives
 * - Future requirements may necessitate stricter matching criteria using additional fields
 */
export function isValidLocationMatch(
  locationA: LocationPartial,
  locationB: LocationPartial,
) {
  if (locationA.state === STATE_MAPPINGS['DISTRICT OF COLUMBIA']) {
    // Public records for DC are not in a good shape, skip state comparison
    return true;
  }

  if (!locationA.state || !locationB.state) {
    // If either side of this computation replied with a partial result, return
    // a match as this is the desired result
    return true;
  }

  // At minimum confirm states match
  return locationA.state === locationB.state;
}

/**
 * Extracts unit number from an address string
 * Handles common formats like "#123", "Unit 123", "Apt 123", etc.
 * @param address The full address string
 * @returns Object with separated address and unit number
 */
export function extractUnitNumber(address: string): {
  address: string;
  unitNumber: string;
} {
  // Common unit number patterns
  const unitPatterns = [
    /#\s*(\w+)/, // #123, #A, etc.
    /(?:Unit|Apt|Apartment|Suite|Ste)\s+(\w+)/i, // Unit 123, Apt 123, etc.
  ];

  let cleanAddress = address;
  let unitNumber = '';

  for (const pattern of unitPatterns) {
    const match = cleanAddress.match(pattern);
    if (match) {
      unitNumber = match[1];
      // Remove unit number from address
      cleanAddress = cleanAddress.replace(match[0], '').trim();
      break;
    }
  }

  return { address: cleanAddress, unitNumber };
}

/* Sanitizes a tax ID by removing special characters that could break API calls
 * @param taxId The raw tax ID string
 * @returns A sanitized version of the tax ID
 */
export function sanitizeTaxId(taxId: string): string {
  // Remove hash symbols and other problematic characters
  return taxId.replace(/#/g, '');
}

export function buildBoundingBox(point: Point): string {
  return `${point.x},${point.y},300000`;
}
